import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import { SafeUrl } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { EventDetailDto, EventSoftwareTypeEnum, RaceDetailDto, RaceStatusEnum, ResultsStatusEnum } from '@aoe/client';
import { Store } from '@ngrx/store';
import { Marker } from 'leaflet';
import {
  distinctUntilChanged,
  filter,
  interval,
  map,
  Observable,
  of,
  startWith,
  Subject,
  switchMap,
  take,
  takeUntil
} from 'rxjs';

import { raceStatusLabel } from 'src/app/core/helpers/Race.helper';
import { selectRaceDetail, selectRaceId, selectRaceList, selectRaceLoading } from 'src/app/core/store/race/race.selector';

import { timestampToTimeString } from 'src/app/core/helpers/Date.helpers';
import { RaceService } from 'src/app/core/services/race.service';
import { selectEventSelected } from 'src/app/core/store/event/event.selector';
import { raceActions } from 'src/app/core/store/race/race.actions';
import { HeaderModule } from '../shared/component/header/header.module';
import { MapComponent } from '../shared/component/map/map.component';
import { PartnersModule } from '../shared/component/partners/partners.module';
import { ProfilOption, RaceProfilComponent } from '../shared/component/race-profil/race-profil.component';
import { RankingComponent } from '../shared/component/ranking/ranking.component';
import { LoadingSpinnerModule } from '../shared/design-system/loading-spinner/loading-spinner.module';
import { VjsPlayerModule } from '../shared/design-system/vjs-player/vjs-player.module';
import { RaceDetailsComponent } from './race-details/race-details.component';

@Component({
  selector: 'aoe-race',
  templateUrl: './race.component.html',
  styleUrls: ['./race.component.scss'],
  imports: [
    CommonModule,
    MapComponent,
    RaceProfilComponent,
    HeaderModule,
    RankingComponent,
    PartnersModule,
    VjsPlayerModule,
    LoadingSpinnerModule,
    RaceDetailsComponent
  ],
  changeDetection: ChangeDetectionStrategy.Default
})
export class RaceComponent implements OnInit, AfterViewInit, OnDestroy {

  private readonly destroy$ = new Subject<void>();

  @ViewChild('profilDiv') profilDiv!: ElementRef<HTMLDivElement>;

  public points!: Array<any>;
  public event?: EventDetailDto | null;
  public hoverDataCoordinate!: { lat: number, lon: number } | null;
  public errors = false;

  timeFromStart$!: Observable<string>;
  private readonly eventConfigType!: EventSoftwareTypeEnum;

  race: RaceDetailDto | null = null;
  displayDetails = false;

  polyline: any;

  // Observables
  readonly isLoading$ = this.store.select(selectRaceLoading);
  readonly selectedRace$ = this.store.select(selectRaceDetail);

  // Propriétés stabilisées (remplacent les getters à risque)
  raceProfilWidth = 0;
  profilOptions: ProfilOption = { width: 0, height: 300, color: 'blue', weight: 3 };

  private resizeObserver?: ResizeObserver;

  constructor(
    readonly activatedRoute: ActivatedRoute,
    private readonly store: Store,
    private readonly raceService: RaceService,
    private readonly cd: ChangeDetectorRef
  ) { }

  ngOnInit(): void {

    // 1. Chargement de la liste des courses pour l'événement sélectionné
    this.store.select(selectEventSelected).pipe(
      filter((event): event is NonNullable<typeof event> => Boolean(event?.id)),
      take(1)
    ).subscribe(event => {
      this.event = event;
      this.store.dispatch(raceActions.loadRace({ payload: { eventId: event.id } }));
    });

    // 2. Chargement des détails de la course par son ID (Correction : souscription explicite)
    this.store.select(selectRaceId).pipe(
      switchMap(raceId => {
        // Cas 1 : L'ID existe déjà dans le store -> on l'utilise directement
        if (raceId) {
          return of(raceId);
        }

        // Cas 2 : Pas d'ID dans le store -> secours via le slug de l'URL
        return this.activatedRoute.paramMap.pipe(
          map(params => params.get('raceSlug')), // ou 'slug' selon le nom de ton paramètre de route
          filter((slug): slug is string => Boolean(slug)),
          distinctUntilChanged(),
          switchMap(slug => this.store.select(selectRaceList).pipe(
            map(races => races?.find(r => r.slug === slug)?.id),
            filter((id): id is string => Boolean(id))
          ))
        );
      }),
      filter((raceId): raceId is string => Boolean(raceId)),
      distinctUntilChanged(), // Évite de re-charger la même course si l'ID ne change pas
      takeUntil(this.destroy$)
    ).subscribe(raceId => {
      this.store.dispatch(raceActions.loadRaceById({ payload: { id: raceId } }));
    });

    // 3. Mise à jour continue des données de la course dans le composant
    this.selectedRace$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(race => {
      this.race = race;
      this.displayDetails = !!race?.distance || !!race?.elevation || !!race?.geojson || !!race?.description;
      if (race?.geojson) {
        const json = JSON.parse(race.geojson as any);
        console.log(json);
        this.polyline = [json.features?.[0].geometry.coordinates.map((c: any) => [c[1], c[0]])];
      }
      this.updateProfilOptions();
    });

    // 4. Chrono réactif (Instancié immédiatement au niveau racine)
    this.timeFromStart$ = this.selectedRace$.pipe(
      switchMap(race => {
        if (!race?.startTime) {
          return of('00:00:00');
        }
        const dateTimeRace = new Date(race.startTime).getTime();
        return interval(1000).pipe(
          startWith(0),
          map(() => timestampToTimeString(Date.now() - dateTimeRace))
        );
      }),
      takeUntil(this.destroy$)
    );
  }

  ngAfterViewInit(): void {
    // Écoute réactive de la taille du conteneur sans surcharger la Change Detection
    if (this.profilDiv?.nativeElement) {
      this.resizeObserver = new ResizeObserver(entries => {
        for (const entry of entries) {
          const width = entry.contentRect.width;
          if (width !== this.raceProfilWidth) {
            this.raceProfilWidth = width;
            this.updateProfilOptions();
            this.cd.detectChanges();
          }
        }
      });
      this.resizeObserver.observe(this.profilDiv.nativeElement);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.resizeObserver?.disconnect();
    this.store.dispatch(raceActions.resetSelectedRace());
  }

  private updateProfilOptions(): void {
    this.profilOptions = {
      width: this.raceProfilWidth,
      height: 300,
      color: this.raceColor ?? 'blue',
      weight: 3
    };
  }

  get isOnStartConfig(): boolean {
    return this.eventConfigType === EventSoftwareTypeEnum.START;
  }

  // get polyline(): any | null {
  //   console.log(this.race?.geojson);

  //   if (this.race?.geojson) {
  //     return [(<any>this.race.geojson)?.features?.[0].geometry.coordinates.map((c: any) => [c[1], c[0]])];
  //   }
  //   return null;
  // }

  get raceName(): string | null {
    return this.race?.name ?? null;
  }

  get raceId(): string | null {
    return this.race?.id ?? null;
  }

  get raceDistance(): number | null {
    return this.race?.distance ?? null;
  }

  get raceElevation(): number | null {
    return this.race?.elevation ?? null;
  }

  get raceProfilData(): any | null {
    return this.race?.profil ?? null;
  }

  get raceLocation() {
    return { start: this.race?.location?.start, arrival: this.race?.location?.finish ?? this.race?.location?.start };
  }

  get pictureLink(): SafeUrl {
    if (this.race?.picture)
      return this.raceService.getPictureLink(this.race.id, this.race.eventId);
    return '/assets/img/default_event_picture.png';
  }

  get raceStatus(): string | null {
    if (this.race?.status) {
      return raceStatusLabel(this.race.status);
    }
    return null;
  }

  get raceStartTime(): string | null {
    return this.race?.startTime ?? this.race?.date ?? null;
  }

  get isInProgress(): boolean {
    return this.race?.status === RaceStatusEnum.IN_PROGRESS;
  }

  get raceStatusCode(): RaceStatusEnum | null {
    return this.race?.status ?? null;
  }

  get resultsStatus(): ResultsStatusEnum | null {
    return this.race?.resultStatus ?? null;
  }

  get raceColor(): string | null {
    return this.race?.color ?? null;
  }

  get partnerId(): string | null {
    return this.event?.partners ?? null;
  }

  get video(): string | null {
    return this.event?.video ?? null;
  }

  get markers(): Marker[] {
    return this.points
      ?.filter(p => !!p.coordinates && !!p.coordinates.lat && !!p.coordinates.lng)
      .map(p => new Marker([p.coordinates!.lat, p.coordinates!.lng], { title: `${p.name}` })) ?? [];
  }


}